import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

export interface SalesChannel {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export const useSalesChannels = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: salesChannels = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sales-channels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_channels")
        .select("*")
        .order("name");

      if (error) throw error;
      return data as SalesChannel[];
    },
  });

  const createSalesChannel = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from("sales_channels")
        .insert({ name })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-channels"] });
      toast({
        title: "Canal de venta creado",
        description: "El canal de venta ha sido creado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo crear el canal de venta.",
        variant: "destructive",
      });
    },
  });

  const updateSalesChannel = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from("sales_channels")
        .update({ name, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-channels"] });
      toast({
        title: "Canal de venta actualizado",
        description: "El canal de venta ha sido actualizado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "No se pudo actualizar el canal de venta.",
        variant: "destructive",
      });
    },
  });

  const deleteSalesChannel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sales_channels")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-channels"] });
      toast({
        title: "Canal de venta eliminado",
        description: "El canal de venta ha sido eliminado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el canal de venta.",
        variant: "destructive",
      });
    },
  });

  return {
    salesChannels,
    isLoading,
    error,
    createSalesChannel: createSalesChannel.mutate,
    updateSalesChannel: updateSalesChannel.mutate,
    deleteSalesChannel: deleteSalesChannel.mutate,
    isCreating: createSalesChannel.isPending,
    isUpdating: updateSalesChannel.isPending,
    isDeleting: deleteSalesChannel.isPending,
  };
};
